import React from 'react';
import { Link } from 'react-router-dom';
import { X, Droplets, WrenchIcon, ThermometerSun, Factory, HeartPulse } from 'lucide-react';

interface ServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const services = [
  {
    icon: <Droplets className="h-6 w-6" />,
    title: 'Pool Maintenance',
    description: `Keeping your pool in perfect condition year-round requires expert care. Our routine maintenance services include:

Water testing & chemical balancing to keep the pool safe and clean
Filter & pump inspections to ensure optimal performance
Debris removal & vacuuming for crystal-clear water
Algae treatment & prevention`,
    slug: 'pool-maintenance'
  },
  {
    icon: <WrenchIcon className="h-6 w-6" />,
    title: 'Repairs & Equipment',
    description: `Is your pool equipment not working properly? We provide fast and efficient repairs for:

Pumps, filters, and skimmers
Pool leaks and plumbing issues
Lights and electrical components

We diagnose and fix problems quickly, so you can enjoy your pool without worries.`,
    slug: 'repairs-equipment'
  },
  {
    icon: <ThermometerSun className="h-6 w-6" />,
    title: 'Heat Pumps',
    description: 'Energy-efficient pool heating solutions for year-round swimming.',
    slug: 'heat-pumps'
  },
  {
    icon: <Factory className="h-6 w-6" />,
    title: 'New Pool Installation',
    description: `We specialize in complete pool installation, ensuring a fully functional and high-quality setup. Whether it's a residential or commercial pool, our team handles:

Piping & filtration systems
Pump, skimmer, and drainage installation
Lighting, heating, and automation setup

We do not provide pool design but work closely with builders and architects to install everything your pool needs to function perfectly.

Get in touch today for a consultation!`,
    slug: 'new-pool-installation'
  },
  {
    icon: <HeartPulse className="h-6 w-6" />,
    title: 'Water Care',
    description: `Balanced water = a healthy pool. We offer:

Professional water testing & analysis
Chemical treatment for safety & hygiene
Algae removal & prevention
Saltwater system maintenance

Say goodbye to murky, unsafe water—we keep it crystal clear!`,
    slug: 'water-care'
  }
];

const ServicesModal: React.FC<ServicesModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />
      
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div 
            className="relative w-full max-w-4xl rounded-xl bg-white shadow-2xl transform transition-all"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
              aria-label="Close modal"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Content */}
            <div className="p-6">
              <h2 id="modal-title" className="text-3xl font-bold text-gray-900 mb-6">
                Our Services
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service, index) => (
                  <Link
                    key={index}
                    to={`/services/${service.slug}`}
                    onClick={onClose}
                    className="group bg-white rounded-xl border border-gray-100 p-6 hover:border-cyan-200 hover:shadow-lg transition-all duration-300 flex flex-col"
                  >
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-cyan-50 rounded-lg text-cyan-600 group-hover:bg-cyan-100 group-hover:text-cyan-700 transition-colors duration-300">
                        {service.icon}
                      </div>
                      <h3 className="ml-4 text-xl font-semibold text-gray-900 group-hover:text-cyan-600 transition-colors duration-300">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                      {service.description}
                    </p>
                  </Link>
                ))}
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg text-gray-600 text-sm text-center">
                Need help choosing the right service? Call us at <a className="text-cyan-600 hover:text-cyan-700" href="tel:+35799562552">+357 99 562 552</a> or email <a className="text-cyan-600 hover:text-cyan-700" href="mailto:info@aquabluecyprus.com">info@aquabluecyprus.com</a>.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicesModal; 